import React, { useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { Close, CameraAlt, OpenInNew } from '@mui/icons-material';

/**
 * Displays a thumbnail image that opens a full-screen dialog on click.
 * Props:
 *   src   – image URL
 *   label – caption shown in the overlay and dialog footer
 *   icon  – small MUI icon shown alongside the label
 */
export default function PhotoCard({ src, label, icon }) {
  const [full, setFull] = useState(false);

  if (!src) return null;

  return (
    <>
      {/* Thumbnail */}
      <Box
        onClick={() => setFull(true)}
        sx={{
          flex: 1, minWidth: 120, borderRadius: '12px', overflow: 'hidden',
          position: 'relative', cursor: 'zoom-in',
          border: '1px solid rgba(16,117,186,0.12)',
          boxShadow: '0 2px 8px rgba(15,33,55,0.08)',
          '&:hover .ph-overlay': { opacity: 1 },
          '&:hover img': { transform: 'scale(1.04)' },
        }}
      >
        <Box
          component="img" src={src} alt={label}
          sx={{ width: '100%', height: 140, objectFit: 'cover', display: 'block', transition: 'transform 0.3s ease' }}
        />

        {/* Hover overlay */}
        <Box className="ph-overlay" sx={{
          position: 'absolute', inset: 0,
          background: 'rgba(16,117,186,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: 0, transition: 'opacity 0.2s',
        }}>
          <CameraAlt sx={{ color: '#fff', fontSize: 28 }} />
        </Box>

        {/* Label bar */}
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
          px: 1.25, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5,
        }}>
          <Box sx={{ color: '#fff', display: 'flex', fontSize: 13 }}>{icon}</Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#fff', fontWeight: 600 }}>{label}</Typography>
        </Box>
      </Box>

      {/* Full-screen dialog */}
      <Dialog
        open={full} onClose={() => setFull(false)} maxWidth="lg"
        PaperProps={{
          sx: {
            background: 'rgba(10,18,28,0.97)', border: 'none',
            borderRadius: 3, overflow: 'hidden', maxWidth: '90vw',
          },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={() => setFull(false)}
            sx={{
              position: 'absolute', top: 10, right: 10, zIndex: 2,
              color: '#fff', background: 'rgba(0,0,0,0.4)',
              '&:hover': { background: 'rgba(0,0,0,0.7)' },
            }}
          >
            <Close />
          </IconButton>

          <Box
            component="a" href={src} target="_blank" rel="noreferrer"
            sx={{ position: 'absolute', top: 10, right: 52, zIndex: 2 }}
          >
            <IconButton sx={{ color: '#fff', background: 'rgba(0,0,0,0.4)', '&:hover': { background: 'rgba(0,0,0,0.7)' } }}>
              <OpenInNew fontSize="small" />
            </IconButton>
          </Box>

          <Box
            component="img" src={src} alt={label}
            sx={{ display: 'block', maxHeight: '80vh', maxWidth: '85vw', objectFit: 'contain' }}
          />

          <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: '#aaa', display: 'flex' }}>{icon}</Box>
            <Typography sx={{ color: '#ccc', fontSize: '0.85rem' }}>{label}</Typography>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
